import { Title, SimpleGrid, Stack } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import {
  CertaintyTextInput,
  CertaintySelect,
  CertaintyDateInput,
} from "./CertaintyInput";
import { useStates, useCities } from "../../hooks/useReferenceData";

interface PatientInfoFormProps {
  form: UseFormReturnType<any>;
  certainties?: Record<string, number>;
  onCertaintyChange?: (field: string, certainty: number) => void;
  missingFields?: string[];
}

export default function PatientInfoForm({
  form,
  certainties = {},
  onCertaintyChange,
}: PatientInfoFormProps) {
  const { data: states = [] } = useStates();
  const selectedState = form.values.patient?.address?.state;
  const { data: cities = [] } = useCities(selectedState);

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <CertaintyTextInput
          label="First Name"
          placeholder="First name"
          required
          showReviewBadge
          reviewTooltip="Verify patient first name is correct"
          name="patient.firstName"
          {...form.getInputProps("patient.firstName")}
          certainty={certainties["patient.firstName"]}
          onCertaintyChange={(c) => onCertaintyChange?.("patient.firstName", c)}
        />
        <CertaintyTextInput
          label="Last Name"
          placeholder="Last name"
          required
          showReviewBadge
          reviewTooltip="Verify patient last name is correct"
          name="patient.lastName"
          {...form.getInputProps("patient.lastName")}
          certainty={certainties["patient.lastName"]}
          onCertaintyChange={(c) => onCertaintyChange?.("patient.lastName", c)}
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <CertaintyDateInput
          label="Date of Birth"
          placeholder="MM/DD/YYYY"
          required
          showReviewBadge
          reviewTooltip="Verify patient date of birth"
          name="patient.dateOfBirth"
          {...form.getInputProps("patient.dateOfBirth")}
          certainty={certainties["patient.dateOfBirth"]}
          onCertaintyChange={(c) =>
            onCertaintyChange?.("patient.dateOfBirth", c)
          }
        />
        <CertaintySelect
          label="Sex"
          placeholder="Select sex"
          required
          showReviewBadge
          reviewTooltip="Verify patient sex"
          name="patient.sex"
          data={[
            { value: "M", label: "Male" },
            { value: "F", label: "Female" },
            { value: "Other", label: "Other" },
          ]}
          {...form.getInputProps("patient.sex")}
          certainty={certainties["patient.sex"]}
          onCertaintyChange={(c) => onCertaintyChange?.("patient.sex", c)}
        />
        <CertaintyTextInput
          label="Medical Record Number"
          placeholder="MRN"
          required
          showReviewBadge
          reviewTooltip="Verify medical record number"
          name="patient.medicalRecordNumber"
          {...form.getInputProps("patient.medicalRecordNumber")}
          certainty={certainties["patient.medicalRecordNumber"]}
          onCertaintyChange={(c) =>
            onCertaintyChange?.("patient.medicalRecordNumber", c)
          }
        />
      </SimpleGrid>

      <Title order={5} mt="xs">
        Contact Information
      </Title>

      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <CertaintyTextInput
          label="Mobile Phone"
          placeholder="(555) 555-5555"
          showReviewBadge
          reviewTooltip="Please verify mobile phone number"
          {...form.getInputProps("patient.mobilePhone")}
          certainty={certainties["patient.mobilePhone"]}
          onCertaintyChange={(c) =>
            onCertaintyChange?.("patient.mobilePhone", c)
          }
        />
        <CertaintyTextInput
          label="Home Phone"
          placeholder="(555) 555-5555"
          showReviewBadge
          reviewTooltip="Please verify home phone number"
          {...form.getInputProps("patient.homePhone")}
          certainty={certainties["patient.homePhone"]}
          onCertaintyChange={(c) => onCertaintyChange?.("patient.homePhone", c)}
        />
        <CertaintyTextInput
          label="Work Phone"
          placeholder="(555) 555-5555"
          showReviewBadge
          reviewTooltip="Please verify work phone number"
          {...form.getInputProps("patient.workPhone")}
          certainty={certainties["patient.workPhone"]}
          onCertaintyChange={(c) => onCertaintyChange?.("patient.workPhone", c)}
        />
      </SimpleGrid>

      <CertaintyTextInput
        label="Email"
        placeholder="patient@example.com"
        type="email"
        showReviewBadge
        reviewTooltip="Please verify email address"
        {...form.getInputProps("patient.email")}
        certainty={certainties["patient.email"]}
        onCertaintyChange={(c) => onCertaintyChange?.("patient.email", c)}
      />

      <Title order={5} mt="xs">
        Address
      </Title>

      <CertaintyTextInput
        label="Street Address"
        placeholder="123 Main St"
        {...form.getInputProps("patient.address.address")}
        certainty={certainties["patient.address.address"]}
        onCertaintyChange={(c) =>
          onCertaintyChange?.("patient.address.address", c)
        }
      />

      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <CertaintySelect
          label="State"
          placeholder="Select state"
          data={states}
          searchable
          {...form.getInputProps("patient.address.state")}
          certainty={certainties["patient.address.state"]}
          onCertaintyChange={(c) =>
            onCertaintyChange?.("patient.address.state", c)
          }
          onChange={(value) => {
            form.setFieldValue("patient.address.state", value);
            // Clear city when state changes
            form.setFieldValue("patient.address.city", "");
          }}
        />
        <CertaintySelect
          label="City"
          placeholder="Select city"
          data={cities}
          searchable
          disabled={!selectedState}
          {...form.getInputProps("patient.address.city")}
          certainty={certainties["patient.address.city"]}
          onCertaintyChange={(c) =>
            onCertaintyChange?.("patient.address.city", c)
          }
        />
        <CertaintyTextInput
          label="ZIP Code"
          placeholder="12345"
          {...form.getInputProps("patient.address.zipCode")}
          certainty={certainties["patient.address.zipCode"]}
          onCertaintyChange={(c) =>
            onCertaintyChange?.("patient.address.zipCode", c)
          }
        />
      </SimpleGrid>
    </Stack>
  );
}
